import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MessageSquare, Send, Trash2, CornerDownRight } from "lucide-react";
import { formatLocalDateTime } from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

/**
 * Threaded comments for a shipment. One level of replies (parent/child).
 */
export default function CommentsThread({ shipmentId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    try {
      const { data } = await api.get(`/shipments/${shipmentId}/comments`);
      setItems(data);
    } catch {} finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [shipmentId]);

  const post = async (text, parentId) => {
    setPosting(true);
    try {
      await api.post(`/shipments/${shipmentId}/comments`, { text, parent_id: parentId || null });
      load();
      if (parentId) {
        setReplyTo(null);
        setReplyText("");
      } else {
        setDraft("");
      }
    } catch {
      toast.error("Failed to post comment");
    } finally {
      setPosting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/shipments/${shipmentId}/comments/${id}`);
      setItems((curr) => curr.filter((c) => c.id !== id && c.parent_id !== id));
    } catch {
      toast.error("Failed to delete (only the author can delete)");
    }
  };

  const tops = items.filter((c) => !c.parent_id);
  const childrenOf = (id) => items.filter((c) => c.parent_id === id);

  return (
    <div className="rg-card p-5 mb-4" data-testid="comments-thread">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-5 w-5 text-slate-700" />
        <h2 className="font-display text-lg uppercase text-slate-900">
          Comments ({items.length})
        </h2>
      </div>

      <div className="flex flex-col gap-2 mb-4">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-testid="comment-input"
          rows={2}
          className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-sm bg-white focus:border-slate-900 outline-none"
          placeholder="Add a comment for the team…"
        />
        <button
          onClick={() => draft.trim() && post(draft.trim())}
          disabled={posting || !draft.trim()}
          data-testid="comment-post-btn"
          className="rg-tap self-end px-4 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-display uppercase tracking-wider rounded-sm flex items-center gap-2 border-2 border-slate-900"
        >
          <Send strokeWidth={2.5} className="h-4 w-4" />
          Post
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading comments…</div>
      ) : tops.length === 0 ? (
        <div className="text-sm text-slate-500" data-testid="comments-empty">No comments yet — be the first.</div>
      ) : (
        <ul className="space-y-3" data-testid="comments-list">
          {tops.map((c) => (
            <li
              key={c.id}
              data-testid={`comment-${c.id}`}
              className="border-l-4 border-slate-300 pl-3"
            >
              <CommentBody c={c} canDelete={user?.id === c.author_id} onDelete={() => remove(c.id)} />
              <button
                type="button"
                onClick={() => { setReplyTo(c.id); setReplyText(""); }}
                className="text-xs font-bold uppercase tracking-wide text-slate-500 hover:text-slate-900 mt-1 flex items-center gap-1"
                data-testid={`comment-reply-btn-${c.id}`}
              >
                <CornerDownRight className="h-3 w-3" /> Reply
              </button>

              {/* Replies */}
              <ul className="mt-2 ml-4 space-y-2">
                {childrenOf(c.id).map((r) => (
                  <li
                    key={r.id}
                    data-testid={`comment-reply-${r.id}`}
                    className="border-l-2 border-slate-200 pl-3"
                  >
                    <CommentBody c={r} canDelete={user?.id === r.author_id} onDelete={() => remove(r.id)} />
                  </li>
                ))}
              </ul>

              {/* Reply composer */}
              {replyTo === c.id && (
                <div className="mt-2 ml-4 flex flex-col gap-2" data-testid={`reply-composer-${c.id}`}>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={2}
                    autoFocus
                    placeholder="Reply…"
                    className="w-full border-2 border-slate-300 rounded-sm px-3 py-2 text-sm bg-white focus:border-slate-900 outline-none"
                    data-testid={`reply-input-${c.id}`}
                  />
                  <div className="flex gap-2 self-end">
                    <button
                      onClick={() => { setReplyTo(null); setReplyText(""); }}
                      className="rg-tap px-3 border-2 border-slate-200 rounded-sm font-bold uppercase tracking-wide text-xs text-slate-700"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => replyText.trim() && post(replyText.trim(), c.id)}
                      disabled={posting || !replyText.trim()}
                      data-testid={`reply-post-btn-${c.id}`}
                      className="rg-tap px-4 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white font-display uppercase tracking-wider rounded-sm flex items-center gap-2 border-2 border-slate-900 text-xs"
                    >
                      <Send strokeWidth={2.5} className="h-3.5 w-3.5" />
                      Post reply
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CommentBody({ c, canDelete, onDelete }) {
  return (
    <>
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <span className="font-bold text-slate-900">{c.author_name}</span>
        <span>·</span>
        <span>{formatLocalDateTime(c.created_at)}</span>
        {canDelete && (
          <button
            onClick={onDelete}
            data-testid={`comment-delete-btn-${c.id}`}
            className="ml-auto text-slate-400 hover:text-rose-700"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <div className="text-sm text-slate-800 whitespace-pre-wrap">{c.text}</div>
    </>
  );
}
